import {Tenant} from "../model/Tenant";
import {User} from "../model/User";
import {Group} from "../model/Group";
import {Asset, Audit, Task, Ticket, Artifact, Tag, Call} from "../model";
import {login, logout as logoutMutation, refreshToken} from "./query/auth";
import {addUserGroup, deleteUserGroup} from "./query/userGroup";
import {EndpointBuilder} from "@reduxjs/toolkit/dist/query/endpointDefinitions";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import {Any} from "../types";
import {
  addEntity,
  advancedSearch,
  deleteEntityById,
  findEntityById,
  updateEntity,
} from "./query/queryEntity";
import {generateCrudApi} from "./crud";
import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {RootState} from "@core/store";
import {resetCredentials} from "./query/resetCredentials";
import {updatePermissions} from "./query/updatePermissions";
import {
  addTicketMessage,
  calculatePerformance,
  updateTicketStatus,
} from "./query/ticket";
import {updateUserInfo} from "./query/updateUserInfo";
import {fetchUserDetail} from "./query/userDetail";
import {searchMeasures} from "./query/measure";
import {copyAsset, fetchAssetTree} from "./query/asset";
import {
  deleteDocuments,
  downloadDocument,
  findDocuments,
  tagDocument,
  uploadDocuments,
} from "./query/document";
import {fetchTasksByAssetId, fetchTasksByRange} from "./query/task";
import {fetchPrognosis, fetchPrognosisLatest} from "./query/prognosis";
import {
  addArtifactTask,
  deleteArtifactTask,
  updateArtifactTask,
  updateArtifactTaskChecklist,
  findAssignedArtifactTasks,
  answerArtifactChecklistItem,
} from "./query/artifact";
import {
  updateTenantSettings,
  clearDisk,
  clearMeasures,
  fetchTenant,
  updateTenantMenu,
} from "./query/tenant";
import {search} from "./query/search";
import {downloadFile, previewFile, uploadFile, deleteFiles} from "./query/file";
import {fetchDiagnosis, fetchDiagnosisLatest} from "./query/diagnosis";
import {fetchCallsByRange} from "./query/call";
import {
  cancelChatAction,
  confirmChatAction,
  createChatSession,
  deleteChatKey,
  deleteChatSession,
  fetchChatProviderModels,
  fetchChatSession,
  fetchChatSessions,
  reindexChatKnowledge,
  fetchChatSettings,
  fetchChatStatus,
  fetchChatSuggestions,
  renameChatSession,
  saveChatKey,
  sendChatMessage,
  updateChatSettings,
} from "./query/chatbot";
import {Goods} from "@core/model/Goods";

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl: window.location.origin,
  credentials: "include", // Invia i cookie HttpOnly automaticamente
  prepareHeaders: (headers, {endpoint, getState}) => {
    const state = getState() as RootState;
    const tenantId = state.app.tenant?.id;
    const accessToken = state.auth.accessToken;
    const isSuperAdmin = state.auth.user?.role === "ADMIN";
    if (accessToken && endpoint.indexOf("getCustomer") === -1) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    if (isSuperAdmin && tenantId) {
      headers.set("X-Sw-Tenant-Id", tenantId);
    }
    return headers;
  },
});

export const api = createApi({
  reducerPath: "api",
  tagTypes: [
    "file",
    "user",
    "tenant",
    "audit",
    "document",
    "ticket",
    "sensor",
    "asset",
    "task",
    "measure",
    "prognosis",
    "diagnosis",
    "artifact",
    "tag",
    "goods",
    "call",
    "chatbot",
  ],
  baseQuery: baseQuery,
  endpoints: builder => ({
    // Generic
    addEntity: addEntity(builder),
    updateEntity: updateEntity(builder),
    deleteEntityById: deleteEntityById(builder),
    findEntityById: findEntityById(builder),
    advancedSearch: advancedSearch(builder),
    search: search(builder),
    // Auth
    login: login(builder),
    logout: logoutMutation(builder),
    refreshToken: refreshToken(builder),
    resetCredentials: resetCredentials(builder),
    updateUserInfo: updateUserInfo(builder),
    // User
    fetchUserDetail: fetchUserDetail(builder),
    // Tenant
    fetchTenant: fetchTenant(builder),
    updateTenantMenu: updateTenantMenu(builder),
    updateTenantSettings: updateTenantSettings(builder),
    clearDisk: clearDisk(builder),
    clearMeasures: clearMeasures(builder),
    // Group
    addUserGroup: addUserGroup(builder),
    deleteUserGroup: deleteUserGroup(builder),
    updatePermissions: updatePermissions(builder),
    // Ticket
    addTicketMessage: addTicketMessage(builder),
    updateTicketStatus: updateTicketStatus(builder),
    calculatePerformance: calculatePerformance(builder),
    // Measure
    searchMeasures: searchMeasures(builder),
    // Asset
    fetchAssetTree: fetchAssetTree(builder),
    copyAsset: copyAsset(builder),
    // Document
    uploadDocuments: uploadDocuments(builder),
    downloadDocument: downloadDocument(builder),
    deleteDocuments: deleteDocuments(builder),
    tagDocument: tagDocument(builder),
    findDocuments: findDocuments(builder),
    // File
    previewFile: previewFile(builder),
    uploadFile: uploadFile(builder),
    downloadFile: downloadFile(builder),
    deleteFiles: deleteFiles(builder),
    // Task
    fetchTasksByRange: fetchTasksByRange(builder),
    fetchTasksByAssetId: fetchTasksByAssetId(builder),
    // Prognosis
    fetchPrognosis: fetchPrognosis(builder),
    fetchPrognosisLatest: fetchPrognosisLatest(builder),
    // Diagnosis
    fetchDiagnosis: fetchDiagnosis(builder),
    fetchDiagnosisLatest: fetchDiagnosisLatest(builder),
    // Call
    fetchCallsByRange: fetchCallsByRange(builder),
    // Chatbot
    fetchChatProviderModels: fetchChatProviderModels(builder),
    sendChatMessage: sendChatMessage(builder),
    fetchChatStatus: fetchChatStatus(builder),
    fetchChatSuggestions: fetchChatSuggestions(builder),
    fetchChatSettings: fetchChatSettings(builder),
    updateChatSettings: updateChatSettings(builder),
    saveChatKey: saveChatKey(builder),
    deleteChatKey: deleteChatKey(builder),
    reindexChatKnowledge: reindexChatKnowledge(builder),
    confirmChatAction: confirmChatAction(builder),
    cancelChatAction: cancelChatAction(builder),
    fetchChatSessions: fetchChatSessions(builder),
    fetchChatSession: fetchChatSession(builder),
    createChatSession: createChatSession(builder),
    renameChatSession: renameChatSession(builder),
    deleteChatSession: deleteChatSession(builder),
    // Artifact
    addArtifactTask: addArtifactTask(builder),
    updateArtifactTask: updateArtifactTask(builder),
    deleteArtifactTask: deleteArtifactTask(builder),
    updateArtifactTaskChecklist: updateArtifactTaskChecklist(builder),
    findAssignedArtifactTasks: findAssignedArtifactTasks(builder),
    answerArtifactChecklistItem: answerArtifactChecklistItem(builder),
  }),
});

export const tenantApi = generateCrudApi<Tenant>("tenant", "/api/admin/tenant");
export const userApi = generateCrudApi<User>("user", "/api/user");
export const groupApi = generateCrudApi<Group>("group", "/api/group");
export const auditApi = generateCrudApi<Audit>("audit", "/api/audit");
export const ticketApi = generateCrudApi<Ticket>("ticket", "/api/ticket");
export const assetApi = generateCrudApi<Asset>("asset", "/api/asset");
export const taskApi = generateCrudApi<Task>("task", "/api/task");
export const tagApi = generateCrudApi<Tag>("tag", "/api/tag");
export const goodsApi = generateCrudApi<Goods>("goods", "/api/goods");
export const callApi = generateCrudApi<Call>("call", "/api/call");
export const artifactApi = generateCrudApi<Artifact>(
  "artifact",
  "/api/artifact",
);

export type LoginRequest = {
  username: string;
  password: string;
};

type tagTypes =
  | "file"
  | "user"
  | "tenant"
  | "audit"
  | "document"
  | "ticket"
  | "sensor"
  | "asset"
  | "task"
  | "measure"
  | "prognosis"
  | "diagnosis"
  | "artifact"
  | "tag"
  | "goods"
  | "call"
  | "chatbot";

export type ApiBuilder = EndpointBuilder<
  BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    Any,
    FetchBaseQueryMeta
  >,
  tagTypes,
  "api"
>;
